import { z } from 'zod';
import { PERMISSION_KEYS, ROLES, type Permission, type Role } from './access.js';

export const PASSWORD_MIN_LENGTH = 10;

const email = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(z.email({ message: 'Некорректный email' }))
  .pipe(z.string().max(255));

const password = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Пароль должен быть не короче ${PASSWORD_MIN_LENGTH} символов`)
  .max(200, 'Слишком длинный пароль');

/** Убирает табуляции и повторяющиеся пробелы (в старой базе их много). */
export const cleanText = (value: string) => value.replace(/\s+/g, ' ').trim();

const name = z.string().transform(cleanText).pipe(z.string().min(1, 'Обязательное поле').max(255));
const optionalText = z
  .string()
  .transform(cleanText)
  .pipe(z.string().max(255))
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();

export const loginSchema = z.object({
  email,
  password: z.string().min(1, 'Введите пароль').max(200),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const forgotPasswordSchema = z.object({ email });
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

export const resetPasswordSchema = z.object({
  token: z.string().min(20).max(200),
  password,
});
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Введите текущий пароль').max(200),
  newPassword: password,
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

const role = z.enum(ROLES);
const permissionList = z
  .array(z.enum(PERMISSION_KEYS as [Permission, ...Permission[]]))
  .max(PERMISSION_KEYS.length);

export const createUserSchema = z.object({
  email,
  fullName: name,
  role: role.default('employee'),
  departmentId: z.uuid().nullable().optional(),
  position: optionalText,
  managerId: z.uuid().nullable().optional(),
  permissions: permissionList.default([]),
  /** Если не задан, пользователь получит письмо со ссылкой для установки пароля. */
  password: password.optional(),
});
export type CreateUserInput = z.input<typeof createUserSchema>;

export const updateUserSchema = z
  .object({
    email,
    fullName: name,
    role,
    departmentId: z.uuid().nullable(),
    position: optionalText,
    managerId: z.uuid().nullable(),
    isActive: z.boolean(),
    permissions: permissionList,
  })
  .partial();
export type UpdateUserInput = z.input<typeof updateUserSchema>;

export const listUsersQuerySchema = z.object({
  search: z.string().trim().max(100).optional(),
  role: role.optional(),
  departmentId: z.uuid().optional(),
  active: z.enum(['true', 'false', 'all']).default('true'),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListUsersQuery = z.input<typeof listUsersQuerySchema>;

export const departmentSchema = z.object({
  name,
  headUserId: z.uuid().nullable().optional(),
});
export type DepartmentInput = z.input<typeof departmentSchema>;

export const paginationSchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

// ---- Ответы API ----

export interface SessionUser {
  id: string;
  email: string;
  fullName: string;
  role: Role;
  departmentId: string | null;
  departmentName: string | null;
  position: string | null;
  permissions: Permission[];
}

export interface UserDto {
  id: string;
  email: string;
  fullName: string;
  role: Role;
  departmentId: string | null;
  departmentName: string | null;
  position: string | null;
  managerId: string | null;
  managerName: string | null;
  isActive: boolean;
  permissions: Permission[];
  lastSeenAt: string | null;
  createdAt: string;
}

export interface DepartmentDto {
  id: string;
  name: string;
  headUserId: string | null;
  headName: string | null;
  userCount: number;
}

export interface Page<T> {
  items: T[];
  total: number;
}

export interface AuditEntryDto {
  id: string;
  actorId: string | null;
  actorName: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  before: unknown;
  after: unknown;
  ip: string | null;
  createdAt: string;
}

export interface EmailLogDto {
  id: string;
  toAddress: string;
  subject: string;
  status: 'pending' | 'sending' | 'sent' | 'failed';
  attempts: number;
  lastError: string | null;
  createdAt: string;
  sentAt: string | null;
}

export interface NotificationDto {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  link: string | null;
  createdAt: string;
  readAt: string | null;
}

export interface ApiError {
  statusCode: number;
  message: string;
  /** Ошибки по полям формы: путь поля → текст. */
  fields?: Record<string, string>;
}
